import {
  Eraser,
  Minus,
  MousePointer2,
  MoveHorizontal,
  MoveVertical,
  Pencil,
  RectangleHorizontal,
  Ruler,
  Type,
} from 'lucide-react'

import type { DrawingTool } from '@/stores/layoutStore'
import { useLayoutStore } from '@/stores/layoutStore'
import { cn } from '@/utils/cn'

interface ToolDef {
  id: DrawingTool
  icon: React.ReactNode
  label: string
  shortcut?: string
}

const TOOLS: ToolDef[] = [
  { id: 'cursor', icon: <MousePointer2 className="h-4 w-4" />, label: 'Cursor', shortcut: 'V' },
  { id: 'crosshair', icon: <Pencil className="h-4 w-4" />, label: 'Freehand', shortcut: 'C' },
]

const DRAWING_TOOLS: ToolDef[] = [
  {
    id: 'trendline',
    icon: <Minus className="h-4 w-4 -rotate-45" />,
    label: 'Trend Line',
    shortcut: 'L',
  },
  {
    id: 'hline',
    icon: <MoveHorizontal className="h-4 w-4" />,
    label: 'Horizontal Line',
    shortcut: 'H',
  },
  {
    id: 'vline',
    icon: <MoveVertical className="h-4 w-4" />,
    label: 'Vertical Line',
    shortcut: 'J',
  },
  {
    id: 'rectangle',
    icon: <RectangleHorizontal className="h-4 w-4" />,
    label: 'Rectangle',
    shortcut: 'R',
  },
  { id: 'text', icon: <Type className="h-4 w-4" />, label: 'Text Note', shortcut: 'T' },
  { id: 'measure', icon: <Ruler className="h-4 w-4" />, label: 'Measure', shortcut: 'M' },
]

const ACTION_TOOLS: ToolDef[] = [
  { id: 'eraser', icon: <Eraser className="h-4 w-4" />, label: 'Eraser', shortcut: 'E' },
]

function ToolButton({
  tool,
  active,
  onClick,
}: {
  tool: ToolDef
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${tool.label}${tool.shortcut ? ` (${tool.shortcut})` : ''}`}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded text-tv-text-secondary transition-colors hover:bg-tv-bg-tertiary hover:text-tv-text-primary',
        active && 'bg-tv-bg-tertiary text-tv-blue'
      )}
    >
      {tool.icon}
    </button>
  )
}

export function DrawingToolbar() {
  const { drawingToolbarOpen, activeTool, setActiveTool } = useLayoutStore()

  if (!drawingToolbarOpen) return null

  return (
    <aside className="flex w-9 shrink-0 flex-col items-center gap-0.5 border-r border-tv-border bg-tv-bg-secondary py-2">
      {TOOLS.map((t) => (
        <ToolButton
          key={t.id}
          tool={t}
          active={activeTool === t.id}
          onClick={() => setActiveTool(t.id)}
        />
      ))}

      <div className="my-1 w-5 border-t border-tv-border" />

      {DRAWING_TOOLS.map((t) => (
        <ToolButton
          key={t.id}
          tool={t}
          active={activeTool === t.id}
          onClick={() => setActiveTool(t.id)}
        />
      ))}

      <div className="my-1 w-5 border-t border-tv-border" />

      {ACTION_TOOLS.map((t) => (
        <ToolButton
          key={t.id}
          tool={t}
          active={activeTool === t.id}
          onClick={() => setActiveTool(t.id)}
        />
      ))}
    </aside>
  )
}
